import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDriverLocationSharing } from '../../hooks/useDriverLocationSharing';
import api from '../../api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Car, Menu, X, LogOut, History, TrendingUp, Clock, Star,
  MapPin, IndianRupee, ArrowLeft, Shield, Landmark, KeyRound,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { playNotificationPing } from '../../lib/notificationSound';
import { PhotoPicker } from './PhotoPicker';
import { DocumentPicker } from './DocumentPicker';
import { photoSrc } from '../../lib/photos';
import { BrandLogo } from '../BrandLogo';

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [isAvailable, setIsAvailable] = useState(user?.available || false);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [earnings, setEarnings] = useState({ today: 0, week: 0, trips_today: 0, trips_month: 0 });
  const [rating, setRating] = useState(user?.rating || 5.0);
  const [totalTrips, setTotalTrips] = useState(user?.totalTrips || 0);
  const { status: locStatus, lastPushedAt } = useDriverLocationSharing(isAvailable);
  const seenPendingIds = React.useRef(new Set());

  const locationLabel = (() => {
    if (locStatus === 'granted' && lastPushedAt) return 'Sharing live location — you receive requests within 5 km';
    if (locStatus === 'denied' || locStatus === 'unsupported') return 'Location off — showing requests from all areas';
    return 'Getting your location…';
  })();

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.get('/api/driver/pending-bookings');
      const list = data || [];
      // Pop-up notification: alert the driver the instant a NEW nearby ride
      // request shows up (not on the very first load, only on new arrivals).
      const freshIds = list.map((b) => b.id);
      if (seenPendingIds.current.size > 0) {
        const newOnes = list.filter((b) => !seenPendingIds.current.has(b.id));
        newOnes.forEach((b) => {
          playNotificationPing();
          toast({
            title: 'New Ride Request Nearby!',
            description: `${b.pickup_location} → ${b.drop_location} · ${b.hours}hr · ₹${b.amount}`,
            duration: 8000,
          });
        });
      }
      seenPendingIds.current = new Set(freshIds);
      setPendingBookings(list);
    } catch {
      setPendingBookings([]);
    }
  }, []);

  const fetchEarnings = useCallback(async () => {
    try {
      const [eRes, meRes] = await Promise.all([
        api.get('/api/driver/earnings'),
        api.get('/api/auth/driver/me'),
      ]);
      setEarnings(eRes.data);
      setRating(meRes.data?.rating ?? 5.0);
      setTotalTrips(meRes.data?.total_trips ?? 0);
      updateUser({
        available: meRes.data?.available,
        hasLicenseDoc: meRes.data?.has_license_doc ?? false,
        hasAadhaarDoc: meRes.data?.has_aadhaar_doc ?? false,
      });
    } catch { /* ignore */ }
  }, [updateUser]);

  // Initial load + auto-redirect if there is an active trip
  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await api.get('/api/driver/active-trip');
        if (data && ['assigned', 'arriving', 'started'].includes(data.status)) {
          navigate('/driver/active-trip');
          return;
        }
      } catch { /* ignore */ }
      fetchEarnings();
      fetchPending();
    };
    init();
  }, [fetchEarnings, fetchPending, navigate]);

  // Poll for pending bookings every 5s while available
  useEffect(() => {
    if (!isAvailable) {
      setPendingBookings([]);
      return;
    }
    fetchPending();
    const id = setInterval(fetchPending, 5000);
    return () => clearInterval(id);
  }, [isAvailable, fetchPending]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleToggleAvailability = async (checked) => {
    setIsAvailable(checked);
    try {
      const { data } = await api.post('/api/driver/availability', { available: checked });
      updateUser({ available: data.available });
      toast({
        title: checked ? 'You are now online' : 'You are now offline',
        description: checked
          ? 'You will receive booking requests'
          : 'You will not receive new bookings'
      });
    } catch (err) {
      setIsAvailable(!checked);
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Update failed',
        description: typeof detail === 'string' ? detail : 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    try {
      await api.post(`/api/driver/bookings/${bookingId}/accept`);
      toast({ title: 'Booking Accepted!', description: 'Navigating to trip details...' });
      navigate('/driver/active-trip');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Could not accept',
        description: typeof detail === 'string' ? detail : 'Please try again',
        variant: 'destructive',
      });
      fetchPending();
    }
  };

  const handleRejectBooking = async (bookingId) => {
    try {
      await api.post(`/api/driver/bookings/${bookingId}/reject`);
      toast({ title: 'Booking Rejected', description: 'Looking for more bookings...' });
      fetchPending();
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <BrandLogo variant="compact" className="h-8 w-auto" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide border-l pl-2">Driver</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Available</span>
                <Switch
                  checked={isAvailable}
                  onCheckedChange={handleToggleAvailability}
                  data-testid="driver-availability-toggle"
                />
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowMenu(!showMenu)}>
                {showMenu ? <X /> : <Menu />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {showMenu && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowMenu(false)}>
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-2xl p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="pb-6 border-b">
              <PhotoPicker
                currentSrc={photoSrc(user?.photoUrl)}
                initials={user?.name?.[0] || 'D'}
                size="w-16 h-16"
                testId="driver-photo"
                upload={async (file) => {
                  const form = new FormData();
                  form.append('file', file);
                  const { data } = await api.post('/api/driver/photo', form);
                  return data;
                }}
                onUploaded={(data) => {
                  updateUser({ photoUrl: data.photo_url });
                  toast({ title: 'Photo updated', description: 'Riders will see your new photo' });
                }}
              />
              <div className="mt-3">
                <p className="font-semibold text-lg">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.phone} · {user?.driverId}</p>
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="text-sm font-semibold">{rating?.toFixed(1)}</span>
                  {user?.gender && <span className="text-xs text-muted-foreground capitalize ml-2">{user.gender}</span>}
                </div>
              </div>
            </div>
            <div className="space-y-2 mt-6">
              <Button variant="ghost" className="w-full justify-start" onClick={() => { setShowMenu(false); navigate('/driver/earnings'); }}>
                <TrendingUp className="w-5 h-5 mr-3" />Earnings
              </Button>
              <Button variant="ghost" className="w-full justify-start" onClick={() => { setShowMenu(false); navigate('/driver/history'); }}>
                <History className="w-5 h-5 mr-3" />Trip History
              </Button>
              <Button variant="ghost" className="w-full justify-start" data-testid="menu-bank-details-btn"
                onClick={() => { setShowMenu(false); navigate('/driver/bank-details'); }}>
                <Landmark className="w-5 h-5 mr-3" />Bank Details &amp; Payouts
              </Button>
              <Button variant="ghost" className="w-full justify-start" data-testid="menu-change-password-btn"
                onClick={() => { setShowMenu(false); navigate('/driver/change-password'); }}>
                <KeyRound className="w-5 h-5 mr-3" />Change Password
              </Button>
              <Button variant="ghost" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleLogout}>
                <LogOut className="w-5 h-5 mr-3" />Logout
              </Button>
            </div>

            <div className="pt-6 mt-6 border-t">
              <p className="text-sm font-semibold text-foreground mb-3">KYC Documents</p>
              <div className="space-y-3">
                <DocumentPicker
                  label="Driving license photo"
                  alreadyUploaded={user?.hasLicenseDoc}
                  testId="dash-license-doc"
                  upload={async (file) => {
                    const form = new FormData();
                    form.append('doc_type', 'license');
                    form.append('file', file);
                    const { data } = await api.post('/api/driver/documents', form);
                    return data;
                  }}
                  onUploaded={() => {
                    updateUser({ hasLicenseDoc: true });
                    toast({ title: 'License updated', description: 'Your new driving license photo has been saved' });
                  }}
                />
                <DocumentPicker
                  label="Aadhaar card photo"
                  alreadyUploaded={user?.hasAadhaarDoc}
                  testId="dash-aadhaar-doc"
                  upload={async (file) => {
                    const form = new FormData();
                    form.append('doc_type', 'aadhaar');
                    form.append('file', file);
                    const { data } = await api.post('/api/driver/documents', form);
                    return data;
                  }}
                  onUploaded={() => {
                    updateUser({ hasAadhaarDoc: true });
                    toast({ title: 'Aadhaar updated', description: 'Your new Aadhaar card photo has been saved' });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-center">
              <IndianRupee className="w-8 h-8 mx-auto mb-2 text-green-600" />
              <p className="text-sm text-muted-foreground">Today</p>
              <p className="text-2xl font-bold" data-testid="earnings-today">₹{earnings.today || 0}</p>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-primary" />
              <p className="text-sm text-muted-foreground">This Week</p>
              <p className="text-2xl font-bold">₹{earnings.week || 0}</p>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <History className="w-8 h-8 mx-auto mb-2 text-accent-dark" />
              <p className="text-sm text-muted-foreground">Total Trips</p>
              <p className="text-2xl font-bold">{totalTrips}</p>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <p className="text-sm text-muted-foreground">Rating</p>
              <p className="text-2xl font-bold">{rating?.toFixed(1) ?? '5.0'}</p>
            </div>
          </Card>
        </div>

        <Card className={`p-6 mb-6 ${isAvailable ? 'bg-gradient-to-r from-green-600 to-green-500' : 'bg-gradient-to-r from-primary-dark to-primary'} text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">{isAvailable ? 'You are Online' : 'You are Offline'}</h2>
              <p className="text-white/90">{isAvailable ? 'Ready to accept bookings' : 'Toggle availability to receive bookings'}</p>
              {isAvailable && (
                <p className="text-white/90 text-sm flex items-center gap-2 mt-2" data-testid="driver-location-status">
                  <MapPin className="w-4 h-4" />
                  {locationLabel}
                </p>
              )}
            </div>
            <Car className="w-16 h-16 text-white/50" />
          </div>
        </Card>

        {pendingBookings.length > 0 && isAvailable && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-4">Booking Requests</h2>
            <div className="space-y-4">
              {pendingBookings.map((booking) => (
                <Card key={booking.id} className="p-6 border-2 border-accent/30 bg-accent/10/50" data-testid={`pending-booking-${booking.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-xl mb-1">{booking.hours}hr Driver Booking</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {booking.booking_time === 'later' ? 'Scheduled' : 'Now'} • {booking.hours}hr
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className="bg-primary text-white">
                        <Clock className="w-3 h-3 mr-1" />{booking.hours}hr
                      </Badge>
                      {booking.female_driver_only && (
                        <Badge className="bg-pink-600 text-white" data-testid={`female-only-${booking.id}`}>Female driver requested</Badge>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Pickup</p>
                        <p className="font-medium">{booking.pickup_location}</p>
                        {booking.pickup_distance_km != null && (
                          <p className="text-xs text-primary font-medium" data-testid={`pickup-distance-${booking.id}`}>
                            {booking.pickup_distance_km} km from you
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Drop</p>
                        <p className="font-medium">{booking.drop_location}</p>
                        {booking.route_distance_km != null && (
                          <p className="text-xs text-muted-foreground" data-testid={`route-info-${booking.id}`}>
                            {booking.route_distance_km} km · ~{booking.route_duration_min} min drive
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <IndianRupee className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Earning</p>
                        <p className="font-bold text-xl">₹{booking.amount}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleAcceptBooking(booking.id)}
                      data-testid={`accept-${booking.id}`}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleRejectBooking(booking.id)}
                      data-testid={`reject-${booking.id}`}
                    >
                      Reject
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {pendingBookings.length === 0 && isAvailable && (
          <Card className="p-12 text-center" data-testid="no-bookings-card">
            <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-semibold mb-2">Waiting for bookings...</h3>
            <p className="text-muted-foreground">You'll be notified when new requests arrive</p>
          </Card>
        )}

        {!isAvailable && (
          <Card className="p-12 text-center">
            <Car className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-semibold mb-2">You're Currently Offline</h3>
            <p className="text-muted-foreground mb-4">Turn on availability to start receiving booking requests</p>
            <Button onClick={() => handleToggleAvailability(true)}>Go Online</Button>
          </Card>
        )}

        <Card className="p-6 mt-6 bg-accent/10 border-accent/30">
          <div className="flex items-start gap-4">
            <Shield className="w-12 h-12 text-primary flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-foreground mb-3">Your Safety Matters</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Emergency SOS</h4>
                  <p className="text-sm text-muted-foreground">Instantly alert local police during trips with our SOS button</p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Call Customer</h4>
                  <p className="text-sm text-muted-foreground">Tap the call button on an active trip to dial your customer directly</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 mt-6">
          <h3 className="font-semibold text-lg mb-4">Driver Information</h3>
          <div className="space-y-2">
            <div className="flex justify-between"><span className="text-muted-foreground">Experience:</span><span className="font-semibold">{user?.experience || 'N/A'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">License Number:</span><span className="font-semibold">{user?.drivingLicense || 'N/A'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Verification Status:</span><span className={`font-semibold ${user?.verified ? 'text-green-600' : 'text-yellow-600'}`}>{user?.verified ? 'Verified' : 'Pending'}</span></div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DriverDashboard;
